import { action } from "mobx";
import { observer } from "mobx-react";
import { Button } from "primereact/button";
import React, { useContext } from "react";
import { useQueryClient } from "react-query";
import styled from "styled-components";
import { useLocStore } from "../../../../wsLocalization";
import { LocContext } from "../../../../wsLocalization/LocContext";
import { UserInfoContext } from "../../../../WSUtils/UserInfoContext";
import { concatClassnames } from "../../../../WSUtils/util";
import { CVEditContext } from "../../CVEditContext";
import { CVEditListStyles } from "../../CVEditListStyles";
import { CVEditKnowledge } from "./CVEditKnowledge";
import { CVEditKnowledgeSegmentStore } from "./CVEditKnowledgeSegmentStore";

export interface CVEditKnowledgeListProps {
  onChange: (newValue: CVEditKnowledge[]) => any;
  knowledgeSegmentStore: CVEditKnowledgeSegmentStore;
}

export const CVEditKnowledgeList = observer(
  ({ onChange, knowledgeSegmentStore }: CVEditKnowledgeListProps) => {
    const cvEditContext = useContext(CVEditContext);

    const items = knowledgeSegmentStore.knowledgeField.store.value;

    if (knowledgeSegmentStore.showPlaceholder) return null;

    const groupedMap: { [groupName: string]: CVEditKnowledge[] } = {};

    items.forEach((cveKnowledge) => {
      let group = groupedMap[cveKnowledge.knowledgeFolder];
      if (!group) {
        group = [];
        groupedMap[cveKnowledge.knowledgeFolder] = group;
      }

      group.push(cveKnowledge);
    });

    const grouped = Object.entries(groupedMap).sort((a, b) =>
      a[0] < b[0] ? -1 : 1
    );

    const list = (
      <div>
        {grouped.map(([groupName, knowledge], index) => (
          <div
            key={`${groupName}_${index}`}
            className="cve-knowledge-list-group cve-listing"
          >
            <div className="cve-knowledge-list-group-name cve-listing-left cve-listing-text">
              <div>{groupName}</div>

              <div
                // todo use grid and fit-content() instead
                // all group names are printed here in order to align columns of each row
                aria-hidden="true"
                style={{ visibility: "hidden", height: 0 }}
              >
                {grouped.map(([groupName], index) => (
                  <div key={`${groupName}_${index}`}>{groupName}</div>
                ))}
              </div>
            </div>
            <div className="cve-knowledge-list-group-items cve-listing-right">
              {knowledge
                .sort((a, b) => a.knowledgeTypeId - b.knowledgeTypeId)
                .map((k) => (
                  <div key={k.id} className="cve-knowledge-list-item">
                    <div className="cve-knowledge-list-item-name cve-listing-text">
                      {k.knowledgeType}
                    </div>
                    <div className="cve-listing-buttons">
                      <Buttons
                        cveKnowledge={k}
                        onChange={onChange}
                        knowledgeSegmentStore={knowledgeSegmentStore}
                      />
                    </div>
                  </div>
                ))}
            </div>
          </div>
        ))}
      </div>
    );

    return (
      <CVEditKnowledgeListStyles
        className={concatClassnames([
          "cve-list cve-knowledge-list cve-two-column-list",
          cvEditContext.layoutClassName,
        ])}
        id={knowledgeSegmentStore.knowledgeListId}
        style={{
          height: knowledgeSegmentStore.knowledgeListForcedHeight,
          overflow: knowledgeSegmentStore.knowledgeListForcedHeight
            ? "hidden"
            : undefined,
        }}
      >
        {list}
      </CVEditKnowledgeListStyles>
    );
  }
);

interface ButtonsProps {
  cveKnowledge: CVEditKnowledge;
  onChange: (newValue: CVEditKnowledge[]) => any;
  knowledgeSegmentStore: CVEditKnowledgeSegmentStore;
}

const Buttons = observer(
  ({ cveKnowledge, onChange, knowledgeSegmentStore }: ButtonsProps) => {
    const cvEditContext = useContext(CVEditContext);
    const userInfo = useContext(UserInfoContext);
    const L = useLocStore(LocContext.CV);

    return (
      <Button
        icon="pi pi-trash"
        disabled={cveKnowledge.id < 0}
        title={L.id("eb908d30609e").loc("Fjern kunnskap")}
        className="p-button-rounded p-button-text p-button-icon-only cve-knowledge-list-trashcan"
        onClick={action((evt) => {
          evt.preventDefault();

          const knowledgeFieldStore =
            knowledgeSegmentStore.knowledgeField.store;

          const newValue = knowledgeFieldStore.value.filter(
            (_cveKnowledge) => _cveKnowledge.id !== cveKnowledge.id
          );

          return onChange(newValue);
        })}
      />
    );
  }
);

export const CVEditKnowledgeListStyles = styled(CVEditListStyles)`
  &.cve-knowledge-list {
    &.cve-s {
      .cve-knowledge-list-item {
        margin-left: -0.75rem;
        padding-left: 0.75rem;
      }

      .cve-listing-left {
        font-weight: bold;
        color: var(--text-color-secondary);
      }
    }

    .cve-knowledge-list-group {
      display: flex;
      margin-top: 1rem;
    }

    .cve-knowledge-listing-position {
      font-weight: bold;
    }

    .cve-knowledge-list-group-name {
      padding: 0.5rem 0.5rem 0.5rem 0;
    }

    .cve-knowledge-list-item-name {
      padding: 0.5rem 0;
    }

    .cve-listing-left.cve-knowledge-list-group-name {
      flex-basis: auto;
      flex-shrink: 1;
    }

    .cve-knowledge-list-item {
      display: flex;
      justify-content: space-between;
      align-items: center;

      margin-left: -1rem;
      padding-left: 1rem;

      .cve-listing-buttons {
        float: none;
        margin-top: 0;
      }

      &:nth-child(even) {
        background: var(--surface-50);
      }
    }
  }
`;
